class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = null;
    this.requestSequence = 0;
    this.searchTerm = '';

    if (!this.input || !this.form) return;

    this.form.addEventListener('reset', this.onFormReset.bind(this));
    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener(
      'input',
      debounce(() => this.onChange(), 300)
    );
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('keydown', this.onInputKeydown.bind(this));

    if (this.input.value !== '') {
      this.onFocus();
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.toggleResetButton();

    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFormReset(event) {
    event.preventDefault();

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.searchTerm = '';
    this.cancelPendingRequest();
    this.closeResults(true);
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onInputKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close(true);
    }
  }

  cancelPendingRequest() {
    this.requestSequence += 1;
    this.abortController?.abort();
    this.abortController = null;
  }

  disconnectedCallback() {
    this.cancelPendingRequest();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.trim().toLowerCase();
    this.setLiveRegionLoadingState();

    this.cancelPendingRequest();
    const requestSequence = this.requestSequence;

    if (this.cachedResults[queryKey]) {
      if (requestSequence === this.requestSequence && this.getQuery() === searchTerm) {
        this.renderSearchResults(this.cachedResults[queryKey]);
      }
      return;
    }

    this.abortController = new AbortController();
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Predictive search request failed with status ${response.status}`);
        }

        return response.text();
      })
      .then((text) => {
        if (requestSequence !== this.requestSequence || this.getQuery() !== searchTerm) return;

        const resultsDocument = new DOMParser().parseFromString(text, 'text/html');
        const resultsSection = resultsDocument.querySelector('#shopify-section-predictive-search');
        if (!resultsSection) {
          throw new Error('Predictive search response did not contain a results section');
        }

        const resultsMarkup = resultsSection.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (
          error?.name === 'AbortError' ||
          error?.code === 20 ||
          requestSequence !== this.requestSequence
        ) {
          return;
        }
        this.removeAttribute('results');
        this.restoreLoadingMarkup();
        this.closeResults();
        this.statusElement =
          this.statusElement || this.querySelector('.predictive-search-status');
        this.setLiveRegionText(
          window.themeStrings?.search_error ||
            'Search is unavailable. Submit the form for full results or try again.'
        );
        console.error('Predictive search request failed', error);
      })
      .finally(() => {
        if (requestSequence === this.requestSequence) {
          this.abortController = null;
        }
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.removeAttribute('hidden');
    this.restoreLoadingMarkup();
    this.predictiveSearchResults.setAttribute('aria-busy', 'true');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement || !statusText) return;
    this.statusElement.textContent = statusText;
  }

  refreshParentFocusTrap() {
    Woolman.Utils.refreshTrapFocus?.();
  }

  restoreLoadingMarkup() {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__loading-state">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults || typeof resultsMarkup !== 'string') return;
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
    this.refreshParentFocusTrap();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.predictiveSearchResults.removeAttribute('aria-busy');
    const countElement = this.querySelector(
      '[data-predictive-search-live-region-count-value]'
    );
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  open() {
    if (!this.input || !this.predictiveSearchResults) return;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', 'true');
    this.predictiveSearchResults.removeAttribute('hidden');
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    // Invalidate the request before closing so a slower response cannot reopen
    // the results after Escape or another close action.
    this.cancelPendingRequest();
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = '';
      this.removeAttribute('results');
      this.restoreLoadingMarkup();
    }

    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input?.setAttribute('aria-expanded', 'false');
    this.predictiveSearchResults?.removeAttribute('aria-busy');
    this.predictiveSearchResults?.setAttribute('hidden', '');
    this.predictiveSearchResults?.removeAttribute('style');
    this.refreshParentFocusTrap();
  }

  toggleResetButton() {
    if (!this.resetButton || !this.input) return;
    const resetIsHidden = this.resetButton.classList.contains('hidden');

    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
